import { supabase } from './supabase';

export interface ReportColumn {
  name: string;
  label: string;
  type: 'money' | 'number' | 'dimension';
}

export interface ReportResult {
  formula: string;
  dataset: string;
  timezone: string;
  currency: string;
  since: string;
  until: string;
  columns: ReportColumn[];
  rows: Array<Record<string, string | number>>;
}

/** Calendar month in a timezone, as SINCE/UNTIL dates. offset 0 is this month, -1 is last month. */
export function reportMonthWindow(timeZone: string, offset = 0, now = new Date()): { since: string; until: string } {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const [yearStr, monthStr] = today.split('-');
  let year = Number(yearStr);
  let month = Number(monthStr) + offset;
  while (month < 1) {
    month += 12;
    year -= 1;
  }
  while (month > 12) {
    month -= 12;
    year += 1;
  }
  const since = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const until = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { since, until };
}

/**
 * The month so far, in a timezone. offset 0 is the 1st through today.
 * offset -1 is the 1st of last month through the same day, or the last day of that month when it is shorter.
 */
export function reportMonthToDate(timeZone: string, offset = 0, now = new Date()): { since: string; until: string } {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const day = Number(today.slice(8, 10));
  const month = reportMonthWindow(timeZone, offset, now);
  const lastDay = Number(month.until.slice(8, 10));
  const clipped = Math.min(day, lastDay);
  return {
    since: month.since,
    until: `${month.until.slice(0, 8)}${String(clipped).padStart(2, '0')}`,
  };
}

export async function runReport(businessId: string, query: string): Promise<ReportResult> {
  const { data, error } = await supabase.rpc('run_report', {
    p_business_id: businessId,
    p_query: query,
  });
  if (error) throw new Error(error.message);
  return data as ReportResult;
}

/** A count metric from the first row of a report. */
export function reportNumber(result: ReportResult, metric: string): number {
  const raw = result.rows[0]?.[metric];
  const amount = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

/** total_sales from a one-row sales report, in dollars. */
export function reportMoney(result: ReportResult, metric = 'total_sales'): number {
  const raw = result.rows[0]?.[metric];
  const amount = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}
